import React, { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchCompanyDetails } from "../../../../api/action/company.jsx";
import { getAllProductCategories, getAllProducts } from "../../../../api/action/products.jsx";
import { Loader } from "../../../../components/Loader.jsx";
import FilterPanel from "./FilterPanel.jsx";
import ProductsList from "./ProductTiles.jsx";
import { academyLogger } from "utils/logger";

const SFCategoryProducts = () => {
  const dispatch = useDispatch();
  const fullUrl = window.location.href;
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, 0]);
  const [maxPrice, setMaxPrice] = useState(0);
  const [sortingOrder, setSortingOrder] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});

  const domain = fullUrl.split("/").slice(0, 3).join("/");
  const { name } = useParams();
  const companyPrimaryId = useSelector((state) => state.companyReducer.company?.primaryId);
  const [filteredCategoryProducts, setFilteredCategoryProducts] = useState([]);

  const fetchProducts = useCallback(async (company, manualCategory = null) => {
    try {
      let products;
      products = await getAllProducts(company)(dispatch);
      products = Array.isArray(products) ? products : [];
      const categoryToUse = manualCategory || name;
      if (categoryToUse && categoryToUse !== "All") {
        products = products.filter(
          (product) => product.category?.name === categoryToUse
        );
      }
      setFilteredCategoryProducts(products);
      const optionalAttributes = [];
      products.forEach((product) => {
        product.optionalAttributes?.forEach((attr) => {
          optionalAttributes.push({
            name: attr.name,
            values: attr.values.map((v) => v.value),
          });
        });
      });

      setCategoryProducts(products);
      setIsLoading(false);

      const res = await dispatch(getAllProductCategories(company));
      setCategories([{ code: "All", name: "All" }, ...(Array.isArray(res) ? res : [])]);

      if (products.length > 0) {
        const highestPrice = Math.max(
          ...products.map((product) => parseFloat(product.price.priceAmt))
        );
        const roundedMaxPrice = Math.ceil(highestPrice);
        setMaxPrice(roundedMaxPrice);
        setSelectedPriceRange([0, roundedMaxPrice]);
      } else {
        setMaxPrice(0);
        setSelectedPriceRange([0, 0]);
      }
    } catch (error) {
      academyLogger.error("Error fetching products:", error);
      setIsLoading(false);
    }
  }, [dispatch, name]);

  const handleCategoryChange = (filterVal) => {
    setSelectedCategories((prev) =>
      prev.includes(filterVal.code)
        ? prev.filter((code) => code !== filterVal.code)
        : [...prev, filterVal.code]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedPriceRange([0, maxPrice]);
    setSortingOrder(null);
    setSelectedAttributes({});
  };

  const toggleSortingOrder = (order) => {
    setSortingOrder(order);
  };

  const handleAttributeChange = (attributeName, attributeValue) => {
    setSelectedAttributes((prev) => {
      const updatedAttributes = { ...prev };
      if (updatedAttributes[attributeName]) {
        if (updatedAttributes[attributeName].includes(attributeValue)) {
          updatedAttributes[attributeName] = updatedAttributes[attributeName].filter((val) => val !== attributeValue);
        } else {
          updatedAttributes[attributeName].push(attributeValue);
        }
      } else {
        updatedAttributes[attributeName] = [attributeValue];
      }
      return updatedAttributes;
    });
  };

  const filteredProducts = categoryProducts.filter((product) => {
    const priceAmt = parseFloat(product.price.priceAmt);
    const categoryCode = product.category ? product.category.code : "";

    const attributeMatch = Object.keys(selectedAttributes).every((attrName) => {
      const selectedValues = selectedAttributes[attrName];
      const productValues = product.optionalAttributes?.find((attr) => attr.name === attrName)?.values || [];
      return selectedValues.every((val) => productValues.map((v) => v.value).includes(val));
    });

    return (
      (selectedCategories.length === 0 ||
        selectedCategories.includes(categoryCode) ||
        selectedCategories.includes("All")) &&
      priceAmt >= selectedPriceRange[0] &&
      priceAmt <= selectedPriceRange[1] &&
      attributeMatch
    );
  });

  useLayoutEffect(() => {
    if (!companyPrimaryId) {
      setIsLoading(false);
      return;
    }
    fetchCompanyDetails(domain).then(() => {
      fetchProducts(companyPrimaryId);
    });
  }, [companyPrimaryId, domain, fetchProducts]);

  return (
    <div className="flex flex-col items-center p-4">
      {isLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      ) : (
        <>
          <div className="flex w-full">
            <div className="filter-panel-container w-1/4 bg-white shadow-lg">
              <FilterPanel
                categories={categories}
                selectedCategories={selectedCategories}
                handleCategoryChange={handleCategoryChange}
                sortingOrder={sortingOrder}
                toggleSortingOrder={toggleSortingOrder}
                handleClearFilters={handleClearFilters}
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                maxPrice={maxPrice}
                selectedAttributes={selectedAttributes}
                handleAttributeChange={handleAttributeChange}
                filteredProducts={filteredCategoryProducts}
              />
            </div>

            <div className="flex flex-col w-full overflow-y-auto">
              <ProductsList products={filteredProducts} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SFCategoryProducts;
